import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { motion } from "framer-motion";
import { Truck, AlertCircle, CheckCircle, Loader2, Mail, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { notifyAdmin } from "@/functions/notifyAdmin";
import { trackLead } from "@/lib/pixel";

export default function TransportationFeePage() {
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    order_number: "",
    reason: ""
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_name || !formData.email || !formData.order_number || !formData.reason) {
      setFormError("Please fill in all required fields.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError("Please enter a valid email address.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsProcessing(true);
    setFormError(null);

    try {
      // Notify admin — no payment required, transportation fee is waived.
      await notifyAdmin({
        channel: "admin",
        from_name: "LCTX Transportation Fee",
        subject: `🚚 Transportation Fee Request (No Charge) - ${formData.order_number}`,
        body: `
TRANSPORTATION FEE REQUEST — NO CHARGE (fee waived)

Customer Information:
- Name: ${formData.customer_name}
- Email: ${formData.email}
- Order Number: ${formData.order_number}
- Reason: ${formData.reason}

Please continue processing this order's shipment arrangement.
        `
      });

      // Confirmation email to customer
      await base44.integrations.Core.SendEmail({
        from_name: "LCTX",
        to: formData.email,
        subject: `Transportation Request Confirmation - Order ${formData.order_number}`,
        body: `
Hi ${formData.customer_name},

Thank you — we've received your transportation request for order ${formData.order_number}.

There is no charge for this. Our team will continue with your shipment arrangement.

If you have any questions, please contact us at:
📧 Email: hkjenholtd@gmail.com
📞 Phone: +852-63613177

Best regards,
LCTX Team
        `
      });

      // Meta conversion signal: shipment-arrangement lead submitted.
      trackLead({ content_category: "transportation_fee_form" });

      setSubmitted(true);
    } catch (error) {
      console.error("Transportation fee request error:", error);
      setFormError("Something went wrong submitting your request. Please contact support directly.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl w-full">
          <Card className="border-0 shadow-2xl">
            <CardContent className="pt-16 pb-16 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl"
              >
                <CheckCircle className="w-14 h-14 text-white" />
              </motion.div>
              <h2 className="text-4xl font-black text-gray-900 mb-4">Request Received!</h2>
              <p className="text-xl text-gray-600 mb-8">No payment required — your transportation request has been submitted.</p>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
                <p className="text-green-800 font-semibold text-lg mb-2">📧 Confirmation sent to</p>
                <p className="text-green-900 font-bold text-xl mb-4">{formData.email}</p>
                <p className="text-green-600 text-xs">Order Number: {formData.order_number}</p>
              </div>
              <div className="mt-8 text-sm text-gray-600">
                <p className="mb-2">✅ Request submitted</p>
                <p className="mb-2">✅ Email confirmation sent</p>
                <p>✅ Our team will continue processing your shipment</p>
              </div>
              <Link to={createPageUrl("Home")} className="inline-block mt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-bold"
                >
                  Return to Home
                </motion.button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Truck className="w-10 h-10 text-red-600" />
            <h1 className="text-4xl font-black text-gray-900">Additional Transportation Request</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            This page is for customers who have been contacted by our customer support team about a special delivery arrangement, remote area delivery, or shipping adjustment. There is no charge — simply submit your details below.
          </p>
        </motion.div>

        <Alert className="mb-8 bg-blue-50 border-blue-300 border-2">
          <AlertCircle className="h-5 w-5 text-blue-600" />
          <AlertDescription className="text-blue-900 font-semibold">
            <strong>No payment is required.</strong> This transportation fee has been waived — just submit the form and our team will proceed with your shipment.
          </AlertDescription>
        </Alert>

        <Card className="shadow-2xl border-0">
          <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white p-8">
            <CardTitle className="text-3xl font-black">Shipment Details</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {formError && (
              <Alert className="mb-6 bg-red-50 border-red-300 border-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <AlertDescription className="text-red-900 font-semibold">{formError}</AlertDescription>
              </Alert>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Customer Information</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name">Full Name *</Label>
                    <Input
                      id="customer_name"
                      value={formData.customer_name}
                      onChange={(e) => handleInputChange("customer_name", e.target.value)}
                      placeholder="John Doe"
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="john@example.com"
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="order_number">Order Number *</Label>
                    <Input
                      id="order_number"
                      value={formData.order_number}
                      onChange={(e) => handleInputChange("order_number", e.target.value)}
                      placeholder="e.g., #12345 or ORD-2024-001"
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reason">Reason for Request *</Label>
                    <Textarea
                      id="reason"
                      value={formData.reason}
                      onChange={(e) => handleInputChange("reason", e.target.value)}
                      placeholder="Please describe the reason (e.g., remote area delivery, oversized shipment, etc.)"
                      required
                      className="h-24"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200 text-center">
                <span className="text-2xl font-black text-green-700">No Charge — $0.00</span>
              </div>

              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white h-14 text-base font-semibold rounded-md shadow-sm transition-all flex items-center justify-center gap-3"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit Request</span>
                )}
              </button>

              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-gray-600" />
                  Need Help?
                </h4>
                <p className="text-sm text-gray-700 mb-3">
                  If you have any questions, please contact our support team.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail className="w-4 h-4 text-red-600" />
                    <span>Email: <a href="mailto:hkjenholtd@gmail.com" className="text-red-600 font-semibold hover:underline">hkjenholtd@gmail.com</a></span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Globe className="w-4 h-4 text-red-600" />
                    <span>Website: <a href="https://www.eztirechanger.com" target="_blank" rel="noopener noreferrer" className="text-red-600 font-semibold hover:underline">www.eztirechanger.com</a></span>
                  </div>
                </div>
              </div>

              <div className="text-center text-xs text-gray-500 pt-4 border-t">
                <p>This page is intended only for approved shipping arrangement requests.</p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
